import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { CONNECT4_SPEC } from '../../../games/connect4/spec.ts';
import {
  applyMove,
  createInitial,
  decodeBoard,
  encodeBoard,
  legalMoves,
} from '../../../games/spec/interpreter.ts';
import type { BoardOf, State as EngineState } from '../../../games/spec/interpreter.ts';
import type { GameSpec, RulesSpec } from '../../../games/spec/schema.ts';
import type { EndReason, GameId, MatchResult, Seat } from '../../../shared/domain.ts';
import { DEFAULT_GAME_ID } from '../../../shared/domain.ts';
import { isLobbyCode, normalizeLobbyCode } from '../../../shared/lobbyCode.ts';
import type {
  ChatMessageFrame,
  LobbyWaiter,
  PlayerPublic,
  ServerFrame,
} from '../../../shared/protocol.ts';
import { fetchGameSpec } from '../../lib/api.ts';
import type { ConnectionState, MatchSocket } from '../../lib/socket.ts';
import { decodeGameStatus, decodeSeat, decodeSeats, isValidSnapshot } from './wire.ts';

/** The board as this client holds it: cells are seat numbers, 0 is empty. */
export type Board = BoardOf<RulesSpec>;
type GameState = EngineState<RulesSpec>;

export type MatchPhase =
  | 'connecting'
  | 'idle'
  /**
   * The server says this account is already in a match that this client has
   * not loaded — a reload, or a second tab. Held until the `resync` lands, so
   * the lobby never flashes in front of a game that is still running.
   */
  | 'resuming'
  | 'queued'
  | 'hosting'
  /** In a host's queue, waiting to be picked. Not in a match. */
  | 'waiting'
  | 'playing'
  | 'ended';

/** An open private lobby, as the host sees it. */
export interface HostedLobby {
  code: string;
  joinPath: string;
  expiresAt: string;
  /** Everyone who has scanned in, in the order the host arranged them. */
  waiting: LobbyWaiter[];
}

/** A lobby you are queued in, as the waiting player sees it. */
export interface WaitingIn {
  code: string;
  game: GameId;
  hostName: string;
  /** One-based place in line, re-sent by the server whenever it changes. */
  position: number;
}

export type { State as MatchReducerState };

export interface MatchView {
  phase: MatchPhase;
  matchId: string | null;
  /**
   * The spec being played, and the single source of the board's geometry,
   * seat colours and labels. Never null: it starts as the built-in reference
   * game so there is always something coherent to render.
   */
  spec: GameSpec;
  /** The game the *server* says this match is. May lead `spec` while it loads. */
  gameId: GameId;
  /** True while the spec for `gameId` is still being fetched. */
  loadingRules: boolean;
  board: Board;
  yourSeat: Seat | null;
  toMove: Seat;
  legalColumns: readonly number[];
  /** Rendered translucent while the server has not yet confirmed the drop. */
  pendingColumn: number | null;
  lastMoveIndex: number | null;
  winningLine: readonly number[] | null;
  seats: Record<Seat, PlayerPublic> | null;
  connection: 'live' | 'reconnecting' | 'opponent_away';
  graceEndsAt: string | null;
  result: { result: MatchResult; reason: EndReason } | null;
  queuePosition: number | null;
  /** Set while this client is hosting a private lobby. */
  lobby: HostedLobby | null;
  /** Set while this client is waiting in someone else's lobby. */
  waitingIn: WaitingIn | null;
  /**
   * True when the board on screen is somebody else's game — the match a waiter
   * is queued behind. There is no seat, no legal move and no resign.
   */
  spectating: boolean;
  /**
   * The lobby whose conversation this screen belongs to, when it is not one this
   * client hosts or waits in.
   *
   * Set for the challenger the host just pulled out of the queue: they are
   * playing, but they are still in the room, and their words should reach the
   * people still waiting rather than only their opponent.
   */
  roomCode: string | null;
  /**
   * The queue this client was just playing out of, offered back to them when
   * the game ends.
   *
   * Outlives `roomCode` deliberately. The server ends the match by releasing
   * everyone from the room, which clears `roomCode` — correct, because the
   * conversation has moved on, but it would leave the challenger's only exit
   * being a public queue full of strangers. On a stream the person who just
   * played the host wants back in the host's line, not a random opponent.
   */
  returnTo: string | null;
  /**
   * Chat, oldest first. Carries both scopes: the lobby's continuous
   * conversation and the current match's system lines.
   */
  chat: ChatMessageFrame[];
  me: { userId: string; displayName: string; rating: number } | null;
  /** Last error the server sent, for display. Cleared by the next successful action. */
  notice: string | null;
  seq: number;
}

interface State extends MatchView {
  game: GameState;
  /** Set when the reducer detects it has diverged from the server. */
  needsResync: boolean;
}

type Action =
  | { type: 'connection'; state: ConnectionState }
  | { type: 'frame'; frame: ServerFrame }
  | { type: 'optimistic'; column: number }
  | { type: 'clear_notice' }
  /** The fetched spec for `state.gameId` has arrived. */
  | { type: 'rules_loaded'; spec: GameSpec }
  /** A client-side validation message, shown without a server round trip. */
  | { type: 'local_notice'; message: string }
  /** The host steps out of a finished match, back to the queue they still own. */
  | { type: 'return_to_lobby' };

/**
 * The game a client renders before a server has told it otherwise.
 *
 * Connect 4 is compiled in, so this costs no fetch and gives the lobby a real
 * board to show. It is a starting point, not a privileged game: `match_start`
 * replaces it with whatever is actually being played.
 */
export const FALLBACK_SPEC: GameSpec = CONNECT4_SPEC;

/** Exported so tests can drive the reducer from the same state the hook starts in. */
export function initialState(spec: GameSpec = FALLBACK_SPEC): State {
  const firstSeat = spec.rules.seats[0];
  const game = createInitial(spec.rules, firstSeat);
  return {
    phase: 'connecting',
    matchId: null,
    spec,
    gameId: spec.id,
    loadingRules: false,
    board: game.board,
    game,
    yourSeat: null,
    toMove: firstSeat,
    legalColumns: [],
    pendingColumn: null,
    lastMoveIndex: null,
    winningLine: null,
    seats: null,
    connection: 'reconnecting',
    graceEndsAt: null,
    result: null,
    queuePosition: null,
    lobby: null,
    waitingIn: null,
    spectating: false,
    roomCode: null,
    returnTo: null,
    chat: [],
    me: null,
    notice: null,
    seq: 0,
    needsResync: false,
  };
}

/**
 * Identity of a chat line.
 *
 * `chatSeq` counts within a scope, so a lobby's third message and its match's
 * third message share a number while being different things. Keying on the
 * sequence alone would silently drop one of them.
 */
function chatKey(message: ChatMessageFrame): string {
  return message.scope.kind === 'match'
    ? `match:${message.scope.matchId}:${message.chatSeq}`
    : `lobby:${message.scope.code}:${message.chatSeq}`;
}

/**
 * Appends whatever has not been seen, in arrival order.
 *
 * Order is arrival rather than sequence because two scopes interleave and their
 * counters are independent: sorting by number would shuffle a conversation.
 */
function mergeChat(
  existing: ChatMessageFrame[],
  incoming: readonly ChatMessageFrame[],
): ChatMessageFrame[] {
  const seen = new Set(existing.map(chatKey));
  const added = incoming.filter((message) => !seen.has(chatKey(message)));
  return added.length === 0 ? existing : [...existing, ...added];
}

function viewFrom(state: State, game: GameState, overrides: Partial<State> = {}): State {
  const yourSeat = overrides.yourSeat ?? state.yourSeat;
  const phase = overrides.phase ?? state.phase;
  const rules = (overrides.spec ?? state.spec).rules;
  const isYourTurn = yourSeat !== null && game.toMove === yourSeat && phase === 'playing';
  return {
    ...state,
    ...overrides,
    game,
    board: game.board,
    toMove: game.toMove,
    legalColumns: isYourTurn ? legalMoves(rules, game).map((move) => move.param) : [],
    winningLine: game.status.kind === 'win' ? game.status.line : (overrides.winningLine ?? null),
  };
}

/**
 * The reducer never invents board state.
 *
 * Every disc on screen got there by feeding a server event through the shared
 * `applyMove`. When the server's own `status`/`toMove` disagrees with the result,
 * that is treated as a divergence to be healed by asking for a resync — not as
 * something to paper over locally.
 */
export function matchReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'clear_notice':
      return { ...state, notice: null };

    case 'local_notice':
      return { ...state, notice: action.message };

    case 'return_to_lobby': {
      // Purely a change of screen. The lobby is still open on the server, so
      // nothing needs to be re-requested to show it.
      if (state.lobby === null) return state;
      return {
        ...state,
        phase: 'hosting',
        matchId: null,
        result: null,
        // The match's own lines go with the match; the lobby's conversation is
        // the room the host is walking back into and outlives any one game.
        chat: state.chat.filter((message) => message.scope.kind === 'lobby'),
        notice: null,
      };
    }

    case 'rules_loaded': {
      // A late arrival for a game we have since moved on from must not replace
      // the rules of the match now on screen.
      if (action.spec.id !== state.gameId) return state;

      // The board is rebuilt from the server rather than reinterpreted locally:
      // frames that arrived while the rules were loading were rejected, so the
      // only honest local state is "empty", and `needsResync` asks for truth.
      const game = createInitial(action.spec.rules, action.spec.rules.seats[0]);
      return viewFrom(state, game, {
        spec: action.spec,
        loadingRules: false,
        needsResync: state.matchId !== null,
      });
    }

    case 'connection': {
      if (action.state === 'open') {
        return {
          ...state,
          connection: state.graceEndsAt ? 'opponent_away' : 'live',
          phase: state.phase === 'connecting' ? 'idle' : state.phase,
        };
      }
      return { ...state, connection: 'reconnecting' };
    }

    case 'optimistic': {
      if (state.phase !== 'playing' || state.pendingColumn !== null) return state;
      if (!state.legalColumns.includes(action.column)) return state;
      // The board is deliberately NOT mutated: the pending disc is a separate,
      // translucent layer, so an optimistic move can never diverge from truth.
      return { ...state, pendingColumn: action.column, notice: null };
    }

    case 'frame':
      return reduceFrame(state, action.frame);
  }
}

function reduceFrame(state: State, frame: ServerFrame): State {
  switch (frame.t) {
    case 'hello_ok': {
      const me = {
        userId: frame.userId,
        displayName: frame.displayName,
        rating: frame.rating,
      };

      // The server is still holding a live match for this account. A reloaded
      // page has no memory of it, and dropping the id here is what used to
      // strand people in a lobby that refused every action: `queue` answered
      // `not_in_match`, `host_lobby` answered `already_engaged`, and nothing on
      // screen led back to the board.
      if (frame.activeMatchId !== null && frame.activeMatchId !== state.matchId) {
        return {
          ...state,
          phase: 'resuming',
          matchId: frame.activeMatchId,
          // Nothing of this match has been applied yet, so the resume that
          // follows must ask for everything.
          seq: 0,
          connection: 'live',
          me,
        };
      }

      return {
        ...state,
        phase: state.phase === 'connecting' ? 'idle' : state.phase,
        connection: 'live',
        me,
      };
    }

    case 'queued':
      return {
        ...state,
        phase: 'queued',
        queuePosition: frame.position,
        lobby: null,
        // Looking for a stranger instead: the old room is no longer the offer.
        returnTo: null,
        notice: null,
      };

    case 'queue_left':
      return { ...state, phase: 'idle', queuePosition: null };

    case 'chat':
      return { ...state, chat: mergeChat(state.chat, [frame]) };

    case 'name_changed': {
      // Rename whoever it was, wherever they appear — across however many seats
      // this game declares.
      if (!state.seats) return state;
      const seats = { ...state.seats };
      for (const [seat, player] of Object.entries(seats)) {
        if (player.userId === frame.userId) {
          seats[Number(seat)] = { ...player, displayName: frame.displayName };
        }
      }
      return { ...state, seats };
    }

    case 'lobby_hosted':
      return {
        ...state,
        phase: 'hosting',
        queuePosition: null,
        lobby: {
          code: frame.code,
          joinPath: frame.joinPath,
          expiresAt: frame.expiresAt,
          // Re-hosting the same code keeps the queue that has already built up
          // on screen; a new code starts empty. The server sends the authoritative
          // roster straight after this frame either way.
          waiting: state.lobby?.code === frame.code ? state.lobby.waiting : [],
        },
        notice: null,
      };

    case 'lobby_roster': {
      // A roster for a code we are not showing is stale — a lobby cancelled
      // while its last broadcast was in flight.
      if (state.lobby === null || state.lobby.code !== frame.code) return state;
      return { ...state, lobby: { ...state.lobby, waiting: frame.waiting } };
    }

    /**
     * A match started or ended in the queue this client is standing in.
     *
     * For the two people playing it, this frame is only a binding: it names the
     * room their words belong to. For everyone still waiting it is the cue to
     * show the game they are waiting behind, or to put it away.
     */
    case 'lobby_match': {
      const roomCode =
        frame.matchId !== null
          ? frame.code
          : state.roomCode === frame.code
            ? null
            : state.roomCode;

      // Remembered while the room still names itself, because the frame that
      // ends the match is the one that forgets it.
      const returnTo = frame.matchId !== null ? frame.code : state.returnTo;

      if (state.phase !== 'waiting') return { ...state, roomCode, returnTo };

      if (frame.matchId !== null) {
        // The board itself arrives in the `resync` that follows.
        return { ...state, roomCode, returnTo, spectating: true, matchId: frame.matchId };
      }

      // Back to a queue with no game in it. The board is cleared rather than
      // left frozen on a finished position nobody is playing any more.
      const rules = state.spec.rules;
      return viewFrom(state, createInitial(rules, rules.seats[0]), {
        roomCode,
        returnTo,
        spectating: false,
        matchId: null,
        seats: null,
        result: null,
        seq: 0,
        pendingColumn: null,
        lastMoveIndex: null,
        winningLine: null,
        graceEndsAt: null,
        connection: state.connection === 'opponent_away' ? 'live' : state.connection,
        needsResync: false,
      });
    }

    case 'lobby_waiting':
      return {
        ...state,
        phase: 'waiting',
        queuePosition: null,
        lobby: null,
        waitingIn: {
          code: frame.code,
          game: frame.game,
          hostName: frame.hostName,
          position: frame.position,
        },
        // Back in the line: the offer to return has been taken up.
        returnTo: null,
        notice: null,
      };

    case 'lobby_waiting_left':
      return {
        ...state,
        // Only ever reached from the queue, but a match starting is announced by
        // `match_start` — never clobber a game in progress.
        phase: state.phase === 'waiting' ? 'idle' : state.phase,
        waitingIn: null,
        // Out of the room: no longer watching it, and no longer talking in it.
        ...(state.phase === 'waiting'
          ? { spectating: false, matchId: null, roomCode: null, returnTo: null, chat: [] }
          : {}),
        notice: waitingLeftMessage(frame.reason),
      };

    case 'lobby_closed': {
      return {
        ...state,
        phase: 'idle',
        lobby: null,
        roomCode: null,
        // The room is gone, so there is nothing to go back to.
        returnTo: null,
        chat: [],
        notice: frame.reason === 'expired' ? 'Your game code expired.' : null,
      };
    }

    case 'match_start': {
      // The server names the game; if it is not the one whose rules are loaded,
      // nothing in this frame can be validated yet. Record the id and let the
      // hook fetch it — `rules_loaded` then asks for a resync to fill the board.
      // A null seat is someone watching from the queue. They stay in the
      // waiting room — with the game now on it — rather than being moved to a
      // match screen offering moves they cannot make.
      const watching = frame.yourSeat === null;

      if (frame.game !== state.gameId || frame.game !== state.spec.id) {
        return {
          ...state,
          phase: watching ? state.phase : 'playing',
          spectating: watching,
          matchId: frame.matchId,
          gameId: frame.game,
          loadingRules: true,
          seq: frame.seq,
          queuePosition: null,
          // The lobby outlives the match: the host plays this challenger and
          // comes back to the rest of the queue still standing.
          waitingIn: watching ? state.waitingIn : null,
          result: null,
          pendingColumn: null,
          lastMoveIndex: null,
          winningLine: null,
          seats: null,
          graceEndsAt: null,
          connection: 'live',
          notice: null,
          needsResync: false,
        };
      }

      const rules = state.spec.rules;
      const firstPlayerSeat = decodeSeat(rules, frame.firstPlayerSeat);
      const yourSeat = frame.yourSeat === null ? null : decodeSeat(rules, frame.yourSeat);
      const seats = decodeSeats(rules, frame.seats);
      if (firstPlayerSeat === null || (!watching && yourSeat === null) || seats === null) {
        return { ...state, seq: frame.seq, needsResync: true };
      }

      const game = createInitial(rules, firstPlayerSeat);
      return viewFrom(state, game, {
        phase: watching ? state.phase : 'playing',
        spectating: watching,
        matchId: frame.matchId,
        yourSeat,
        seats,
        seq: frame.seq,
        queuePosition: null,
        waitingIn: watching ? state.waitingIn : null,
        result: null,
        pendingColumn: null,
        lastMoveIndex: null,
        winningLine: null,
        graceEndsAt: null,
        connection: 'live',
        notice: null,
        needsResync: false,
      });
    }

    case 'move_applied': {
      // Ignore anything already applied: a resume replay may redeliver events.
      if (frame.seq <= state.seq) return state;
      // Rules still loading: there is nothing to apply the move to. The resync
      // that follows `rules_loaded` will carry this move anyway.
      if (state.loadingRules) return { ...state, seq: frame.seq };

      const rules = state.spec.rules;
      const seat = decodeSeat(rules, frame.seat);
      if (seat === null) {
        return { ...state, seq: frame.seq, needsResync: true };
      }

      const applied = applyMove(rules, state.game, seat, { param: frame.column });
      if (!applied.ok) {
        return { ...state, seq: frame.seq, needsResync: true };
      }
      // The server's status is the assertion; a mismatch means we have drifted.
      const agrees =
        applied.state.toMove === frame.toMove &&
        applied.primary === frame.landedIndex &&
        JSON.stringify(applied.state.status) === JSON.stringify(frame.status);

      return viewFrom(state, applied.state, {
        seq: frame.seq,
        lastMoveIndex: applied.primary,
        pendingColumn:
          state.pendingColumn !== null && frame.column === state.pendingColumn
            ? null
            : state.pendingColumn,
        needsResync: !agrees,
        notice: null,
      });
    }

    case 'match_end':
      return {
        ...state,
        // A watcher does not leave the room because someone else's game
        // finished; the result is shown on the waiting room's board instead.
        phase: state.spectating ? state.phase : 'ended',
        seq: frame.seq,
        result: { result: frame.result, reason: frame.reason },
        winningLine: frame.line,
        pendingColumn: null,
        legalColumns: [],
        graceEndsAt: null,
        connection: 'live',
      };

    case 'opponent_status': {
      if (state.yourSeat !== null && frame.seat === state.yourSeat) {
        return { ...state, seq: frame.seq };
      }
      return {
        ...state,
        seq: frame.seq,
        graceEndsAt: frame.graceEndsAt,
        connection: frame.state === 'disconnected' ? 'opponent_away' : 'live',
      };
    }

    case 'resync': {
      // The one frame that rebuilds state wholesale, so it gets the full
      // spec-aware check before any of it is trusted.
      if (frame.game !== state.spec.id) {
        // A resync for a game whose rules we do not hold. Record what to fetch
        // rather than validating it against the wrong geometry.
        return {
          ...state,
          seq: frame.seq,
          matchId: frame.matchId,
          gameId: frame.game,
          loadingRules: true,
          needsResync: false,
        };
      }

      const rules = state.spec.rules;
      // As in `match_start`: no seat means watching, not a broken frame.
      const watching = frame.yourSeat === null;
      const yourSeat = frame.yourSeat === null ? null : decodeSeat(rules, frame.yourSeat);
      const seats = decodeSeats(rules, frame.seats);
      const toMove = decodeSeat(rules, frame.snapshot.toMove);
      const status = decodeGameStatus(rules, frame.snapshot.status);
      if (
        (!watching && yourSeat === null) ||
        seats === null ||
        toMove === null ||
        status === null ||
        !isValidSnapshot(rules, frame.snapshot)
      ) {
        return { ...state, seq: frame.seq, needsResync: true };
      }

      const game: GameState = {
        board: decodeBoard(rules, frame.snapshot.board),
        toMove,
        moveCount: frame.snapshot.moveCount,
        status,
      };
      // The backlog is one scope's worth, so ordering it by its own sequence is
      // safe before it joins the rest of the conversation.
      const backlog = [...(frame.chat ?? [])].sort((a, b) => a.chatSeq - b.chatSeq);

      return viewFrom(state, game, {
        chat: mergeChat(state.chat, backlog),
        phase: watching ? state.phase : frame.outcome ? 'ended' : 'playing',
        spectating: watching,
        matchId: frame.matchId,
        yourSeat,
        seats,
        seq: frame.seq,
        pendingColumn: null,
        lastMoveIndex: null,
        graceEndsAt: frame.graceEndsAt,
        connection: frame.opponentConnected ? 'live' : 'opponent_away',
        result: frame.outcome ? { result: frame.outcome.result, reason: frame.outcome.reason } : null,
        needsResync: false,
        notice: null,
      });
    }

    case 'error': {
      // A stale seq is always followed by a resync, so the pending disc just clears.
      return {
        ...state,
        pendingColumn: null,
        // The queue we offered to put them back into is gone. Drop the offer so
        // the button falls back to finding a game rather than failing again.
        ...(frame.code === 'lobby_not_found' ? { returnTo: null } : {}),
        notice: errorMessage(frame.code, frame.message, state),
      };
    }
  }
}

function waitingLeftMessage(reason: 'kicked' | 'left' | 'closed'): string | null {
  switch (reason) {
    case 'kicked':
      return 'The host removed you from the queue.';
    case 'closed':
      return 'That game closed before your turn came up.';
    case 'left':
      // The player pressed Leave. Telling them what they just did is noise.
      return null;
  }
}

function errorMessage(code: string, message: string, state: State): string {
  switch (code) {
    case 'not_your_turn':
      return 'Not your turn yet.';
    case 'illegal_move':
      return 'That move is not allowed.';
    case 'stale_seq':
      return 'Board was out of date. Catching up.';
    case 'rate_limited':
      return 'Slow down a moment.';
    // Sent when the server is holding a match this client is not showing. It
    // must never claim the match is gone: the truth is the opposite, and the
    // client is already resuming into it.
    case 'not_in_match':
      return 'You already have a game in progress.';
    case 'already_engaged':
      return 'You already have a game in progress. Finish or resign it first.';
    case 'protocol_version':
      return 'This page is out of date. Reload to keep playing.';
    case 'not_lobby_host':
      return 'Your game code is no longer open.';
    // Two readings of one code: a host acting on someone who has gone, or a
    // waiter acting on a room they are no longer in. Only the host holds a
    // lobby, so that is what tells them apart.
    case 'not_waiting':
      return state.lobby !== null
        ? 'That player has already left the queue.'
        : "You're not in that queue any more.";
    default:
      return message;
  }
}

export interface UseMatchResult {
  view: MatchView;
  /** @param game Which game to play. Defaults to the reference game. */
  queue(game?: GameId): void;
  leaveQueue(): void;
  hostLobby(game?: GameId): void;
  joinLobby(code: string): void;
  cancelLobby(): void;
  /** Host: nudge one waiter one place up or down the queue. */
  moveWaiter(userId: string, direction: 'up' | 'down'): void;
  /** Host: play this waiter next. */
  startWith(userId: string): void;
  kickWaiter(userId: string): void;
  /** Waiter: withdraw from the queue. */
  leaveLobby(): void;
  /** Host: leave a finished match and go back to the queue that is still open. */
  returnToLobby(): void;
  say(text: string): void;
  setName(name: string): void;
  drop(column: number): void;
  resign(): void;
  dismissNotice(): void;
  /** Debug aid, and what `encodeBoard` is for: a stable board string. */
  boardString(): string;
}

/**
 * @param enabled Only connect once we know there is a session. The `/ws`
 *   handshake rejects anonymous upgrades with 401, so connecting before sign-in
 *   would retry forever, hammer the server, and show the visitor a permanent
 *   "Reconnecting" warning about a connection they never asked for.
 */
export function useMatch(socket: MatchSocket, enabled = true): UseMatchResult {
  const [state, dispatch] = useReducer(matchReducer, undefined, () => initialState());
  const stateRef = useRef(state);
  stateRef.current = state;

  /**
   * Specs already fetched, so a rematch or a reconnect does not re-request
   * rules the client is holding. Seeded with the compiled-in reference game.
   */
  const specCache = useRef(new Map<GameId, GameSpec>([[FALLBACK_SPEC.id, FALLBACK_SPEC]]));

  useEffect(() => {
    if (!enabled) return;
    const offFrame = socket.on('frame', (frame) => dispatch({ type: 'frame', frame }));
    const offState = socket.on('state', (connection) =>
      dispatch({ type: 'connection', state: connection }),
    );
    socket.connect();
    return () => {
      offFrame();
      offState();
      socket.close();
    };
  }, [socket, enabled]);

  /*
   * Keep the transport's resume cursor in step with what has actually been
   * applied.
   *
   * Never for a watched match: a reconnecting socket resumes on its own, and a
   * spectator's subscription does not survive the drop — they are dropped from
   * the queue too — so the resume would come back `not_in_match` for a match
   * they were only watching.
   */
  useEffect(() => {
    if (state.spectating) return;
    if (state.matchId && state.phase !== 'ended') socket.trackMatch(state.matchId, state.seq);
    else if (state.phase === 'ended') socket.forgetMatch();
  }, [socket, state.matchId, state.seq, state.phase, state.spectating]);

  /*
   * A reload comes back with nothing but `hello_ok`, which now carries the
   * match the server still has us in. Ask for it unprompted: getting back into
   * your own game should not need a button, and the board is also the only
   * screen that offers Resign, which is the way back out.
   *
   * `sinceSeq: 0` forces a full `resync` rather than a replay, which is the
   * honest request — a fresh page has no buffered history to extend.
   */
  useEffect(() => {
    if (state.phase !== 'resuming' || state.matchId === null) return;
    socket.send({ t: 'resume', matchId: state.matchId, sinceSeq: 0 });
  }, [socket, state.phase, state.matchId]);

  // Self-healing: if a server event could not be applied, ask for the truth.
  useEffect(() => {
    if (!state.needsResync || !state.matchId) return;
    console.warn('[match] local state diverged from the server; resyncing');
    socket.send({ t: 'resume', matchId: state.matchId, sinceSeq: 0 });
  }, [socket, state.needsResync, state.matchId]);

  /**
   * Fetches the rules for whatever the server says is being played.
   *
   * This is what makes an authored game renderable: the geometry, the seats and
   * the theme all arrive as data keyed off the `game` id in `match_start`.
   */
  useEffect(() => {
    if (!state.loadingRules) return;
    const gameId = state.gameId;

    const cached = specCache.current.get(gameId);
    if (cached) {
      dispatch({ type: 'rules_loaded', spec: cached });
      return;
    }

    const controller = new AbortController();
    fetchGameSpec(gameId, controller.signal)
      .then((spec) => {
        specCache.current.set(gameId, spec);
        dispatch({ type: 'rules_loaded', spec });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // Without rules there is no board to draw, so this has to be said out
        // loud rather than left as an empty grid the player can click at.
        console.error('[match] could not load the rules for', gameId, error);
        dispatch({
          type: 'local_notice',
          message: 'Could not load the rules for this game. Try reloading.',
        });
      });

    return () => controller.abort();
  }, [state.loadingRules, state.gameId]);

  const drop = useCallback(
    (column: number) => {
      const current = stateRef.current;
      if (current.phase !== 'playing' || current.matchId === null) return;
      if (current.pendingColumn !== null) return;
      if (!current.legalColumns.includes(column)) return;

      dispatch({ type: 'optimistic', column });
      socket.send({
        t: 'move',
        matchId: current.matchId,
        baseSeq: current.seq,
        clientMoveId: crypto.randomUUID(),
        move: { column },
      });
    },
    [socket],
  );

  // The game is a parameter, not a constant: hosting is how an authored game
  // reaches a stream, and hard-coding one id here is what used to make the
  // whole authoring engine unreachable from the play path.
  const queue = useCallback(
    (game: GameId = DEFAULT_GAME_ID) => socket.send({ t: 'queue', game }),
    [socket],
  );
  const leaveQueue = useCallback(() => socket.send({ t: 'leave_queue' }), [socket]);
  const hostLobby = useCallback(
    (game: GameId = DEFAULT_GAME_ID) => socket.send({ t: 'host_lobby', game }),
    [socket],
  );
  const cancelLobby = useCallback(() => socket.send({ t: 'cancel_lobby' }), [socket]);
  const moveWaiter = useCallback(
    (userId: string, direction: 'up' | 'down') =>
      socket.send({ t: 'lobby_move', userId, direction }),
    [socket],
  );
  const startWith = useCallback(
    (userId: string) => socket.send({ t: 'lobby_start', userId }),
    [socket],
  );
  const kickWaiter = useCallback(
    (userId: string) => socket.send({ t: 'lobby_kick', userId }),
    [socket],
  );
  const leaveLobby = useCallback(() => socket.send({ t: 'leave_lobby' }), [socket]);
  const returnToLobby = useCallback(() => dispatch({ type: 'return_to_lobby' }), []);
  const setName = useCallback((name: string) => socket.send({ t: 'set_name', name }), [socket]);
  const say = useCallback(
    (text: string) => {
      // Read through the ref, like `drop`: the callback must not be rebuilt on
      // every state change, or the chat input loses focus mid-sentence.
      const { matchId, lobby, waitingIn, roomCode } = stateRef.current;
      // The room comes first. In a lobby the conversation belongs to the queue,
      // not to whichever game happens to be running: scoping it to the match
      // would cut the waiting players out of it and reset it between games.
      const code = lobby?.code ?? waitingIn?.code ?? roomCode;
      if (code) {
        socket.send({ t: 'say', scope: { kind: 'lobby', code }, text });
        return;
      }
      if (!matchId) return;
      socket.send({ t: 'say', scope: { kind: 'match', matchId }, text });
    },
    [socket],
  );
  const joinLobby = useCallback(
    (code: string) => {
      const normalized = normalizeLobbyCode(code);
      // The socket would reject a malformed code anyway; failing here keeps the
      // error next to the input the user is looking at.
      if (!isLobbyCode(normalized)) {
        dispatch({ type: 'local_notice', message: 'That code does not look right.' });
        return;
      }
      socket.send({ t: 'join_lobby', code: normalized });
    },
    [socket],
  );
  const resign = useCallback(() => {
    const current = stateRef.current;
    if (current.matchId && current.phase === 'playing') {
      socket.send({ t: 'resign', matchId: current.matchId });
    }
  }, [socket]);

  const dismissNotice = useCallback(() => dispatch({ type: 'clear_notice' }), []);
  const boardString = useCallback(
    () => encodeBoard(stateRef.current.spec.rules, stateRef.current.board),
    [],
  );

  const view = useMemo<MatchView>(() => {
    const { game: _game, needsResync: _needsResync, ...rest } = state;
    return rest;
  }, [state]);

  return {
    view,
    queue,
    leaveQueue,
    hostLobby,
    joinLobby,
    cancelLobby,
    moveWaiter,
    startWith,
    kickWaiter,
    leaveLobby,
    returnToLobby,
    say,
    setName,
    drop,
    resign,
    dismissNotice,
    boardString,
  };
}

/** The active board's column count, for callers that lay out a grid. */
export function colsOf(spec: GameSpec): number {
  return spec.rules.board.cols;
}
